'use client';

import { type JSX, useCallback, useRef, useState } from 'react';
import type { BookingTunnelProps } from '../types';
import { BookingProvider, useBooking } from '../context/BookingContext';
import { BookingCodesProvider } from '../context/BookingCodesContext';
import { StepNavProvider } from '../context/StepNavContext';
import { BookingProgress } from './BookingProgress';
import { BookingTrustBar } from './BookingTrustBar';
import { BookingSummary } from './BookingSummary';
import { BookingStepRenderer } from './BookingStepRenderer';
import { RecapCard } from './RecapCard';
import { StepFooter } from './StepFooter';
import { RedirectPaymentNotice } from './shared/RedirectPaymentNotice';
import { useStepTransitionFocus } from './shared/useStepTransitionFocus';
import '../styles/booking-tunnel.scss';

export function BookingTunnel(props: BookingTunnelProps) {
  /* « Prendre un autre rendez-vous » : nouvelle génération, le tunnel est remonté de zéro. Le retour de
     paiement Stripe ne concerne que la première : il n'est jamais rejoué. */
  const [generation, setGeneration] = useState(0);
  const restart = useCallback((): void => setGeneration((value): number => value + 1), []);
  const firstGeneration = generation === 0;
  return (
    <BookingProvider
      key={generation}
      onRestart={restart}
      callbacks={props.callbacks}
      bookingParams={props.bookingParams}
      filters={props.filters}
      professionalSlug={props.professionalSlug}
      professionalName={props.professionalName}
      prefilledVoucherCode={props.prefilledVoucherCode}
      prefilledPromoCode={props.prefilledPromoCode}
      locale={props.locale}
      currency={props.currency}
      stripePublishableKey={props.stripePublishableKey}
      onStepChange={props.onStepChange}
      onFunnelEvent={props.onFunnelEvent}
      labels={props.labels}
      successMode={props.successMode}
      flagBaseUrl={props.flagBaseUrl}
      headerAccessory={props.headerAccessory}
      backToSiteUrl={props.backToSiteUrl}
      initialRedirectStatus={firstGeneration ? props.initialRedirectStatus : undefined}
      initialBookingUuid={firstGeneration ? props.initialBookingUuid : undefined}
      handleRedirectReturn={firstGeneration ? props.handleRedirectReturn : false}
    >
      <BookingCodesProvider>
        <StepNavProvider>
          <BookingTunnelInner />
        </StepNavProvider>
      </BookingCodesProvider>
    </BookingProvider>
  );
}

/* Chargement initial (loading → services) : ne pas voler le scroll/focus de la page hôte. */
const skipBookingTransition = (previous: string, next: string): boolean =>
  next === 'loading' || next === 'error' || previous === 'loading';

function BookingTunnelInner(): JSX.Element {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { redirectNotice, isRedirectFinalizing, currentStep } = useBooking();
  useStepTransitionFocus(rootRef, currentStep, skipBookingTransition);

  const redirectPaymentNotice = redirectNotice ? (
    <RedirectPaymentNotice phase={redirectNotice.type} message={redirectNotice.message} />
  ) : null;

  return (
    <div className="bt-root" ref={rootRef}>
      <BookingProgress />
      <BookingTrustBar />
      {isRedirectFinalizing ? (
        <div className="bt-redirect-finalizing-view">{redirectPaymentNotice}</div>
      ) : (
        <>
          {redirectPaymentNotice}
          <div className="bt-layout">
            <div className="bt-main">
              {/* Récap repliable : Regular à chaque étape, Compact à la finalisation (CSS du conteneur `bt`). */}
              <RecapCard />
              <BookingStepRenderer />
            </div>
            {/* Récap sidebar — layout wide uniquement */}
            <BookingSummary variant="sidebar" />
          </div>
          {/* Footer d'action sticky — colonne unique uniquement */}
          <StepFooter />
        </>
      )}
    </div>
  );
}
